/**
 * Доступ к публичному контуру API.
 *
 * Типы импортируются прямо из бэкенда — импорт только типов, в сборку он не
 * попадает, зато форма ответа и её использование не могут разъехаться незаметно.
 */
export type { PublicSnapshot, PublicOrder } from "../../src/services/scope-service";
export type { CartTotals, OrderLine, SelectedModifier } from "../../src/types";
export type { ProductDto } from "../../src/serializers";

import type { CartTotals } from "../../src/types";

export interface Bench {
  id: string;
  label: string;
  status: string;
}

export interface CartTotalsResponse extends CartTotals {
  promoError?: { code: string; message: string };
}

export class ApiError extends Error {
  readonly code: string;
  readonly details: unknown;

  constructor(message: string, code: string, details: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "Content-Type": "application/json", ...init.headers } : init?.headers
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = body as { error?: string; code?: string; details?: unknown };
    throw new ApiError(error.error ?? "Не удалось выполнить запрос", error.code ?? "unknown", error.details);
  }

  return body as T;
}

/**
 * Случайный UUID там, где `crypto.randomUUID` недоступен.
 *
 * Он появился в браузерах только в 2021 году и работает лишь в защищённом
 * контексте. На кухонном планшете со старым webview его нет, и заказ не
 * берётся вовсе. То же самое ждёт установку на мини-ПК: панель там открывается
 * по адресу вида `http://192.168.1.10`, а это не защищённый контекст ни в одном
 * браузере, каким бы новым он ни был.
 *
 * `getRandomValues` есть и там, и там: ограничение защищённого контекста
 * касается `randomUUID` и `subtle`, но не его.
 */
function randomUuid(): string {
  const api = typeof crypto === "undefined" ? undefined : crypto;

  if (typeof api?.randomUUID === "function") return api.randomUUID();

  if (typeof api?.getRandomValues === "function") {
    const bytes = api.getRandomValues(new Uint8Array(16));
    // Версия и вариант — как требует RFC 4122 для UUID v4.
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;

    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20)
    ].join("-");
  }

  /*
   * Совсем древний браузер. Ключ выходит предсказуемым, но от него требуется
   * не тайна, а неповторимость: он отличает повтор одной и той же попытки от
   * второй операции. Отказать здесь значило бы не дать оформить заказ вообще.
   */
  const random = () => Math.random().toString(16).slice(2, 10);
  return `${random()}-${random().slice(0, 4)}-4${random().slice(0, 3)}-a${random().slice(0, 3)}-${random()}${random().slice(0, 4)}`;
}

/**
 * Ключ идемпотентности попытки (п. 30 ТЗ).
 *
 * Гость в парке жмёт «Оплатить» на плохой связи — и жмёт ещё раз. С ключом
 * сервер узнаёт повтор той же попытки и возвращает тот же заказ вместо второго.
 */
function newIdempotencyKey(): string {
  return randomUuid();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body: JSON.stringify(body) }),

  /** Оформление и оплата: повтор обязан вернуть прежний заказ, а не создать новый. */
  once: <T>(path: string, body: unknown, key: string) =>
    request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Idempotency-Key": key }
    }),

  newIdempotencyKey
};
